import TextField from "@material-ui/core/TextField";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import { Grid, IconButton, InputAdornment } from "@mui/material";
import {
  FormInputDatePickerProps,
  FormInputFieldProps,
  FormInputPasswordFieldProps,
  FormInputSelectFieldProps,
} from "../../ts/FormType";
import { renderRequiredError } from "./FormValidator";
import { KeyboardDatePicker, KeyboardDatePickerProps } from "@material-ui/pickers";
import { normalizeTimeForInput, adjustTimeWithArrowKey } from "../../utils/schoolTimeUtils";

export const InputField: React.FC<FormInputFieldProps> = ({
  name,
  type,
  rows,
  multiline,
  className,
  value,
  lable,
  placeHolder,
  handleChange,
  isValidValue,
  isSubmit,
  requiredMessage,
  max,
  min,
  disabled = false,
  isCheckValidEmailValue = false,
  isCheckValidNoValue = false,
  isCheckValidPhoneValue = false,
  isCheckValidRID = false,
  isCheckValidAddress = false,
  isCheckValidIPAddress = false,
  disablePrevious = "",
  disableUpcoming = "",
  isCheckValidPassword = false,
  isCheckString = false,
  isCheckValidPostcode = false,
  isCheckValidVehicleReg = false,
}) => {
  const isTimeInput = type === "time";
  const timeValue = isTimeInput ? normalizeTimeForInput(value) : value;
  const timeMin = isTimeInput ? normalizeTimeForInput(disablePrevious) : "";
  const timeMax = isTimeInput ? normalizeTimeForInput(disableUpcoming) : "";

  const inputProps: React.InputHTMLAttributes<HTMLInputElement> = { step: isTimeInput ? 60 : "any" };

  if (isTimeInput) {
    if (timeMin) inputProps.min = timeMin;
    if (timeMax) inputProps.max = timeMax;
  } else {
    if (typeof max === "number") inputProps.maxLength = max;
    if (typeof min === "number") inputProps.minLength = min;
    if (disablePrevious !== "") inputProps.min = disablePrevious;
    if (disableUpcoming !== "") inputProps.max = disableUpcoming;
  }

  const handleTimeKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isTimeInput || disabled || !handleChange) return;
    if (event.key !== "ArrowUp" && event.key !== "ArrowDown") return;

    event.preventDefault();
    const input = event.currentTarget;
    const currentVal = input.value || String(timeValue || "");
    const newValue = adjustTimeWithArrowKey(
      currentVal,
      event.key,
      1,
      timeMin || undefined,
      timeMax || undefined
    );

    if (newValue === currentVal) return;

    const syntheticTarget = Object.assign(input, {
      value: newValue,
      name: name || input.name,
    });

    handleChange({
      ...event,
      target: syntheticTarget,
      currentTarget: syntheticTarget,
    } as React.ChangeEvent<HTMLInputElement>);
  };

  if (isTimeInput) {
    inputProps.onKeyDown = handleTimeKeyDown;
  }

  return (
    <div className={className}>
      <TextField
        InputLabelProps={{ shrink: true }}
        fullWidth
        minRows={rows}
        multiline={multiline}
        type={type}
        label={lable}
        name={name}
        value={timeValue}
        placeholder={placeHolder}
        onChange={handleChange}
        disabled={disabled}
        inputProps={inputProps}
      />
      {!isValidValue && renderRequiredError(value, isSubmit, requiredMessage, isCheckValidEmailValue, isCheckValidNoValue, isCheckValidPhoneValue, isCheckValidRID, isCheckValidAddress, isCheckValidIPAddress, isCheckValidPassword, isCheckString, isCheckValidPostcode, isCheckValidVehicleReg)}
    </div>
  );
};

export const InputPasswordField: React.FC<FormInputPasswordFieldProps> = ({
  name,
  type,
  rows,
  multiline,
  className,
  value,
  lable,
  placeHolder,
  handleChange,
  isValidValue,
  isSubmit,
  requiredMessage,
  clickToShowPassowrd,
  hoverToPassword,
  showPassword,
  isCheckValidEmailValue = false,
  isCheckValidNoValue = false,
  isCheckValidPhoneValue = false,
  isCheckValidRID = false,
  isCheckValidAddress = false,
  isCheckValidIPAddress = false,
  isCheckValidPassword = false,
  isCheckString = false,
}) => {
  return (
    <div className={className}>
      <TextField
        InputLabelProps={{ shrink: true }}
        fullWidth
        minRows={rows}
        multiline={multiline}
        type={showPassword ? "text" : type}
        label={lable}
        name={name}
        value={value}
        placeholder={placeHolder}
        onChange={handleChange}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label="toggle password visibility"
                onClick={clickToShowPassowrd}
                onMouseDown={hoverToPassword}
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      // disabled={disabled}
      />
      {!isValidValue && renderRequiredError(value, isSubmit, requiredMessage, isCheckValidEmailValue, isCheckValidNoValue, isCheckValidPhoneValue, isCheckValidRID, isCheckValidAddress, isCheckValidIPAddress, isCheckValidPassword, isCheckString)}
    </div>
  );
};

export const InputSelectField: React.FC<FormInputSelectFieldProps> = ({
  select,
  lable,
  name,
  className,
  value,
  options,
  handleChange,
  isValidValue,
  isSubmit,
  requiredMessage,
  disabled = false,
  isCheckValidEmailValue = false,
  isCheckValidNoValue = false,
  isCheckValidPhoneValue = false,
  isCheckValidRID = false,
  isCheckValidAddress = false,
  isCheckValidIPAddress = false,
  isCheckValidPassword = false,
  isCheckString = false,
}) => {
  return (
    <div className={className}>
      <TextField
        InputLabelProps={{ shrink: true }}
        fullWidth
        select={select}
        SelectProps={{
          native: true,
        }}
        label={lable}
        name={name}
        value={value}
        onChange={handleChange}
        disabled={disabled}
      >
        {options.map((option) => (
          <option className={option.name} key={option.id} value={option.id}>
            {option.name}
          </option>
        ))}
      </TextField>
      {!isValidValue && renderRequiredError(value, isSubmit, requiredMessage, isCheckValidEmailValue, isCheckValidNoValue, isCheckValidPhoneValue, isCheckValidRID, isCheckValidAddress, isCheckValidIPAddress, isCheckValidPassword, isCheckString)}
    </div>
  );
};

export const InputDatePicker: React.FC<FormInputDatePickerProps> = ({
  className,
  value,
  lable,
  handleChange,
  isValidValue,
  isSubmit,
  requiredMessage,
  disabled = false,
  isCheckValidEmailValue = false,
  isCheckValidNoValue = false,
  isCheckValidPhoneValue = false,
  isCheckValidRID = false,
  isCheckValidAddress = false,
  isCheckValidIPAddress = false,
  isCheckValidPassword = false,
  isCheckString = false,
  min,
}) => {
  return (
    <div className={className}>
      <Grid container>
        <KeyboardDatePicker
          InputLabelProps={{ shrink: true }}
          fullWidth
          variant="inline"
          openTo="date"
          views={["year", "month", "date"]}
          format="YYYY-MM-DD"
          label={lable}
          className="form-textfield-styles"
          value={value}
          onChange={handleChange}
          placeholder="YYYY-MM-DD"
          minDate={min}
          disabled={disabled}
          KeyboardButtonProps={{ "aria-label": "change date" }}
          autoOk={true}
        />
        {!isValidValue && renderRequiredError(value, isSubmit, requiredMessage, isCheckValidEmailValue, isCheckValidNoValue, isCheckValidPhoneValue, isCheckValidRID, isCheckValidAddress, isCheckValidIPAddress, isCheckValidPassword, isCheckString)}
      </Grid>
    </div>
  );
};